import React, { PropsWithChildren, useEffect, useMemo } from 'react'
import {
  BackendServicesAuthProvider,
  DirectoryContextProvider,
  FeatureFlagsProvider,
  InternalConfigProvider,
  InternalProfileProvider,
  PolicyEvaluatorContextProvider,
  useStorage,
} from '@aserto/console-common'

import { useActiveConfigurationName, useConfigurations } from '../ActiveConfiguration'

const ConsoleCommonProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const config = useConfigurations()
  const { configs, readOnly, ...base } = config
  const { configurationName, setConfigurationName } = useActiveConfigurationName()

  const configFields = useMemo(() => {
    return configs.find((e) => e.name === configurationName)
  }, [configurationName, configs])

  const defaultName = useMemo(() => {
    if (config && config.configs && config.configs.length > 0) {
      return config.configs[0].name || ''
    }

    return ''
  }, [config])

  useEffect(() => {
    if (configurationName === '' || readOnly) {
      setConfigurationName(defaultName)
    }
  }, [defaultName, configurationName, setConfigurationName, readOnly])

  const {
    authorizerServiceUrl,
    authorizerApiKey,
    directoryApiKey,
    directoryServiceUrl,
    directoryReaderServiceUrl,
    directoryWriterServiceUrl,
    directoryModelServiceUrl,
    directoryTenantId,
  } = configFields || {}

  const [tenantId, setTenantId] = useStorage<string | undefined>('tenantId', directoryTenantId)

  return (
    <InternalConfigProvider
      config={{
        ...base,
        authorizerServiceUrl: authorizerServiceUrl || '',
        authorizerApiKey: authorizerApiKey || '',
        directoryApiKey: directoryApiKey || '',
        directoryServiceUrl: directoryServiceUrl || '',
        directoryReaderServiceUrl: directoryReaderServiceUrl || '',
        directoryWriterServiceUrl: directoryWriterServiceUrl || '',
        directoryModelServiceUrl: directoryModelServiceUrl || '',
      }}
    >
      <InternalProfileProvider profile={{ account: null, tenantId: tenantId, setTenantId }}>
        <FeatureFlagsProvider
          featureFlags={{
            isGraphQLEnabled: false,
            hasTemplates: false,
            hasDirectoryApiBrowser: true,
            hasAssertions: configFields?.configurationType === 'central_directory',
          }}
        >
          <PolicyEvaluatorContextProvider>
            <BackendServicesAuthProvider
              backendServicesAuth={{
                getAuthorizerToken: () => Promise.resolve(`basic ${authorizerApiKey}`),
                getDirectoryToken: () => Promise.resolve(`basic ${directoryApiKey}`),
              }}
            >
              <DirectoryContextProvider
                displayState={{
                  canAddObject: { enabled: true, visible: true },
                  canAddRelation: { enabled: true, visible: true },
                  canDeleteDirectory: { enabled: true, visible: true },
                  canEditManifest: { enabled: true, visible: true },
                  canEditObject: { enabled: true, visible: true },
                  canRemoveRelation: { enabled: true, visible: true },
                  canRemoveAssertion: { enabled: true, visible: true },
                  canAddAssertion: { enabled: true, visible: true },
                  canEditAssertion: { enabled: true, visible: true },
                }}
              >
                {children}
              </DirectoryContextProvider>
            </BackendServicesAuthProvider>
          </PolicyEvaluatorContextProvider>
        </FeatureFlagsProvider>
      </InternalProfileProvider>
    </InternalConfigProvider>
  )
}

export default ConsoleCommonProvider
